import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { SeriesBadge } from "@/components/SeriesBadge";
import { SeriesDrilldownDialog } from "@/components/SeriesDrilldownDialog";
import { getSeriesProducts, seriesProductSummary } from "@/data/series-products";
import { trackBadgeClick, trackBadgeHover } from "@/lib/analytics";
import { useBadgeGridImpression, useOnceFlag } from "@/hooks/use-badge-analytics";
import { assetSrcSet, firstAssetUrl } from "@/lib/asset-url";
import hb640w from "@/assets/home-hero-v23-640.webp.asset.json";
import hb960w from "@/assets/home-hero-v23-960.webp.asset.json";
import hb1280w from "@/assets/home-hero-v23-1280.webp.asset.json";
import hb1536w from "@/assets/home-hero-v23-1536.webp.asset.json";
import hb1920w from "@/assets/home-hero-v23-1920.webp.asset.json";
import hb640j from "@/assets/home-hero-v23-640.jpg.asset.json";
import hb960j from "@/assets/home-hero-v23-960.jpg.asset.json";
import hb1280j from "@/assets/home-hero-v23-1280.jpg.asset.json";
import hb1536j from "@/assets/home-hero-v23-1536.jpg.asset.json";
import hb1920j from "@/assets/home-hero-v23-1920.jpg.asset.json";

const homeBannerWebpSrcset = assetSrcSet([
  [hb640w, 640],
  [hb960w, 960],
  [hb1280w, 1280],
  [hb1536w, 1536],
  [hb1920w, 1920],
]);
const homeBannerJpegSrcset = assetSrcSet([
  [hb640j, 640],
  [hb960j, 960],
  [hb1280j, 1280],
  [hb1536j, 1536],
  [hb1920j, 1920],
]);
const homeBannerFallback = firstAssetUrl(hb1920j, hb1536j, hb1280j, hb960j, hb640j);
const homeBanner = homeBannerFallback;


const SERIES_BADGES = [
  { to: "/alamo-series", slug: "alamo-series", name: "Alamo", tag: "Residential" },
  { to: "/hill-country-series", slug: "hill-country-series", name: "Hill Country", tag: "Outdoor Living" },
  { to: "/bluebonnet-series", slug: "bluebonnet-series", name: "Bluebonnet", tag: "Tents & Markets" },
  { to: "/longhorn-series", slug: "longhorn-series", name: "Longhorn", tag: "Commercial" },
  { to: "/republic-series", slug: "republic-series", name: "Republic", tag: "Gov & Schools" },
  { to: "/rio-grande-series", slug: "rio-grande-series", name: "Rio Grande", tag: "Hospitality & Resorts" },
  { to: "/lone-star-elite", slug: "lone-star-elite", name: "Lone Star Series", tag: "Smart Lighting & Automation" },
  { to: "/custom-fabrication", slug: "trail-boss", name: "Trail Boss", tag: "Welding & Fab" },
  { to: "/ranger-care", slug: "ranger-care", name: "Ranger Care", tag: "Membership" },
  { to: "/revival-series", slug: "revival-series", name: "Revival", tag: "Service & Recovery" },
  { to: "/farming-ranching-series", slug: "farming-ranching-series", name: "Farming & Ranching", tag: "Agricultural" },
  { to: "/outdoor-recreation-series", slug: "outdoor-recreation-series", name: "Outdoor Recreation", tag: "Hunt · Fish · Boat" },
] as const;

export interface HomeHeroBannerProps {
  /** Optional city name so city landing pages get localized alt text / copy. */
  city?: string;
  /**
   * Which parts to render.
   * - "full" (default): marketing banner + Series badge grid (city pages)
   * - "banner": just the marketing banner
   * - "badges": just the "Pick Your Series" grid
   */
  variant?: "full" | "banner" | "badges";
}

/**
 * The main marketing banner plus the Signature Collection badge grid.
 * Shared by the home page and every city landing page so customers see the
 * same hero no matter which page they land on.
 */
export function HomeHeroBanner({ city, variant = "full" }: HomeHeroBannerProps) {
  const place = city ? `${city}, Texas` : "Texas";
  const placement = city ? "city_hero_badges" : "home_hero_badges";
  const badgeGridRef = useBadgeGridImpression<HTMLUListElement>(
    placement,
    SERIES_BADGES.map((b) => b.slug),
  );
  const firstTouch = useOnceFlag();
  const [drilldown, setDrilldown] = useState<string | null>(null);
  const showBanner = variant !== "badges";
  const showBadges = variant !== "banner";

  return (
    <>

      {/* HOME BANNER — full marketing graphic under the navigation */}
      {showBanner ? (
      <section className="border-b-8 border-navy-deep bg-navy-deep">

        <picture>
          {homeBannerWebpSrcset ? (
            <source type="image/webp" srcSet={homeBannerWebpSrcset} sizes="100vw" />
          ) : null}
          {homeBannerJpegSrcset ? (
            <source type="image/jpeg" srcSet={homeBannerJpegSrcset} sizes="100vw" />
          ) : null}
          <img
            src={homeBanner}
            alt={`Texas Made Shade & Covers — custom awnings, retractable awnings, sail shades, curtains, covers and fabrication proudly serving ${place}`}
            width={1920}
            height={815}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="block h-auto w-full object-contain"

            onError={(e) => {
              const img = e.currentTarget;
              if (homeBannerFallback && img.src !== homeBannerFallback) {
                img.parentElement?.querySelectorAll("source").forEach((s) => s.remove());
                img.srcset = "";
                img.src = homeBannerFallback;
              }
            }}
          />
        </picture>
      </section>
      ) : null}

      {/* WHERE DO YOU WANT TO GO — Signature Collection badges */}
      {showBadges ? (
      <section className="relative overflow-hidden border-b-8 border-navy-deep bg-navy-deep py-14 text-white lg:py-20">

        <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-10 text-center">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Pick Your Series ★</div>
            <h2 className="mt-3 font-display text-4xl font-bold uppercase leading-tight text-white md:text-5xl">
              Where do you want to go?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-white/85 md:text-lg">
              Every project fits one of our signature series. Tap a badge to jump straight to the collection built for your home, business, project, and style.
            </p>
          </div>
          <ul ref={badgeGridRef} className="mx-auto grid max-w-6xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 sm:gap-4">
            {SERIES_BADGES.map((b) => (
              <li key={b.slug} className="flex flex-col">
                <Link
                  to={b.to}
                  className="group flex w-full flex-col items-center gap-2 rounded-lg border border-white/10 bg-navy-deep/70 p-3 text-center shadow-lg backdrop-blur-sm transition hover:-translate-y-1 hover:border-red/60 hover:bg-navy-deep/90"
                  aria-label={`${b.name} Series — ${b.tag}`}
                  onClick={() =>
                    trackBadgeClick({ slug: b.slug, name: b.name, placement }, b.to)
                  }
                  onMouseEnter={() => {
                    if (firstTouch(b.slug))
                      trackBadgeHover({ slug: b.slug, name: b.name, placement });
                  }}
                  onFocus={() => {
                    if (firstTouch(b.slug))
                      trackBadgeHover({ slug: b.slug, name: b.name, placement });
                  }}
                >
                  <span className="flex h-24 w-full items-center justify-center md:h-28">
                    <SeriesBadge slug={b.slug} size="sm" className="max-h-full w-auto transition group-hover:scale-105" />
                  </span>
                  <span className="flex min-h-[5.5rem] flex-col justify-start">
                    <span className="font-display text-sm font-bold uppercase leading-tight text-white group-hover:text-red md:text-base">
                      {b.name}
                    </span>
                    <span className="mt-1 block font-display text-[10px] uppercase tracking-[0.15em] text-white/70">
                      {b.tag}
                    </span>
                    <span className="mt-1 block text-[10.5px] font-medium leading-snug text-white/75">
                      {seriesProductSummary(b.slug, 4)}
                    </span>
                  </span>
                </Link>
                <button
                  type="button"
                  aria-label={`See all products in the ${b.name} Series`}
                  onClick={() => setDrilldown(b.slug)}
                  className="mt-2 w-full rounded-md border border-white/20 bg-white/5 px-2 py-1.5 font-display text-[10px] font-bold uppercase tracking-[0.12em] text-white/85 transition hover:border-red hover:bg-red/15 hover:text-white"
                >
                  See all {getSeriesProducts(b.slug)?.products.length ?? 0} products
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>
      ) : null}



      <SeriesDrilldownDialog
        slug={drilldown}
        onClose={() => setDrilldown(null)}
        placement={placement}
      />
    </>
  );
}
